import React, { useState, useEffect } from 'react';
import { makeStyles } from '@material-ui/core/styles';
import { 
  TextField, 
  Button, 
  Grid, 
  Typography,
  Paper,
  Snackbar,
} from '@material-ui/core';
import { Alert } from '@material-ui/lab';
import SaveIcon from '@material-ui/icons/Save';
import LockIcon from '@material-ui/icons/Lock';
import { UsersService } from '../../../api/users.services';
import { useSelector } from 'react-redux';

const useStyles = makeStyles((theme) => ({
  root: {
    width: '100%',
    maxWidth: 800,
    margin: '0 auto',
  },
  formSection: {
    padding: theme.spacing(3),
    marginBottom: theme.spacing(3),
  },
  title: {
    marginBottom: theme.spacing(3),
  },
  divider: {
    margin: theme.spacing(3, 0),
  },
  buttonContainer: {
    display: 'flex',
    justifyContent: 'flex-end',
    marginTop: theme.spacing(2),
  },
  button: {
    marginLeft: theme.spacing(1),
  }
}));

export default function MyAccountTab() {
  const classes = useStyles();
  const currentUserConnection = useSelector((state) => state.User);
  const currentUser = currentUserConnection.user ? currentUserConnection.user : null;
  
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState(null);

  const [formData, setFormData] = useState({
    id: currentUser.id,
    user: '',
    email: '',
  });

  const [passwordData, setPasswordData] = useState({
    currentPassword: '',
    newPassword: '',
    confirmPassword: '',
  });

  const [passwordErrors, setPasswordErrors] = useState({
    currentPassword: '',
    newPassword: '',
    confirmPassword: '',
  });

  useEffect(() => {
    // Cargar datos del usuario actual
    if (currentUser) {
      setFormData({
        id: currentUser.id,
        user: currentUser.user || '',
        email: currentUser.email || '',
      });
    }
  }, [currentUser]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData({
      ...formData,
      [name]: value
    });
  };

  const handlePasswordChange = (e) => {
    const { name, value } = e.target;
    setPasswordData({
      ...passwordData,
      [name]: value
    });
    
    // Validar en tiempo real
    validatePasswordField(name, value);
  };

  const validatePasswordField = (fieldName, value) => {
    let errors = { ...passwordErrors };
    
    switch (fieldName) {
      case 'currentPassword':
        errors.currentPassword = value ? '' : 'La contraseña actual es requerida';
        break;
      case 'newPassword':
        errors.newPassword = value.length < 6 ? 'La nueva contraseña debe tener al menos 6 caracteres' : '';
        if (passwordData.confirmPassword && value !== passwordData.confirmPassword) {
          errors.confirmPassword = 'Las contraseñas no coinciden';
        } else {
          errors.confirmPassword = '';
        }
        break;
      case 'confirmPassword':
        errors.confirmPassword = 
          value !== passwordData.newPassword ? 'Las contraseñas no coinciden' : '';
        break;
      default:
        break;
    }
    
    setPasswordErrors(errors);
  };

  const validatePasswordForm = () => {
    const { currentPassword, newPassword, confirmPassword } = passwordData;
    
    if (!currentPassword || !newPassword || !confirmPassword) {
      setPasswordErrors({
        currentPassword: currentPassword ? '' : 'La contraseña actual es requerida',
        newPassword: newPassword ? '' : 'La nueva contraseña es requerida',
        confirmPassword: confirmPassword ? '' : 'La confirmación es requerida'
      });
      return false;
    }
    
    if (newPassword.length < 6) {
      setPasswordErrors({
        ...passwordErrors,
        newPassword: 'La nueva contraseña debe tener al menos 6 caracteres'
      });
      return false;
    }
    
    if (newPassword !== confirmPassword) {
      setPasswordErrors({
        ...passwordErrors,
        confirmPassword: 'Las contraseñas no coinciden'
      });
      return false;
    }
    
    return true;
  };

  const handleSaveProfile = async () => {
    setLoading(true);
    try {
      // Llamar a la API para actualizar el perfil
      await UsersService.updateUserService(formData);
      setSuccess('Perfil actualizado correctamente');
    } catch (err) {
      setError('Error al actualizar el perfil');
      console.error('Error al actualizar perfil:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleChangePassword = async () => {
    if (!validatePasswordForm()) {
      return;
    }

    setLoading(true);
    try {
      // Llamar a la API para cambiar la contraseña
      const success = await UsersService.changeMyPassword({
        currentPassword: passwordData.currentPassword,
        newPassword: passwordData.newPassword
      });

      if (success) {
        // Reiniciar formulario de contraseña
        setPasswordData({
          currentPassword: '',
          newPassword: '',
          confirmPassword: ''
        });

        setSuccess('Contraseña actualizada correctamente');
      } else {
        setError('Error al cambiar la contraseña. Verifica que la contraseña actual sea correcta.');
      }
    } catch (err) {
      setError('Error al cambiar la contraseña. Verifica que la contraseña actual sea correcta.');
      console.error('Error al cambiar contraseña:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleCloseAlert = () => {
    setSuccess(false);
    setError(null);
  };

  return (
    <div className={classes.root}>
      <Typography variant="h5" className={classes.title}>
        Mi Cuenta
      </Typography>
      
      <Paper className={classes.formSection} elevation={1}>
        <Typography variant="h6" gutterBottom>
          Información Personal
        </Typography>
        
        <Grid container spacing={3}>
          <Grid item xs={12} sm={6}>
            <TextField
              fullWidth
              label="Nombre de Usuario"
              name="user"
              value={formData.user}
              onChange={handleChange}
              variant="outlined"
              margin="normal"
            />
          </Grid>
          <Grid item xs={12} sm={6}>
            <TextField
              fullWidth
              label="Email"
              name="email"
              type="email"
              value={formData.email}
              onChange={handleChange}
              variant="outlined"
              margin="normal"
            />
          </Grid>
        </Grid>
        
        <div className={classes.buttonContainer}>
          <Button
            variant="contained"
            color="primary"
            startIcon={<SaveIcon />}
            onClick={handleSaveProfile}
            disabled={loading}
            className={classes.button}
          >
            Guardar Cambios
          </Button>
        </div>
      </Paper>
      
      <Paper className={classes.formSection} elevation={1}>
        <Typography variant="h6" gutterBottom>
          Cambiar Contraseña
        </Typography>
        
        <Grid container spacing={3}>
          <Grid item xs={12}>
            <TextField
              fullWidth
              label="Contraseña Actual"
              name="currentPassword"
              type="password"
              value={passwordData.currentPassword}
              onChange={handlePasswordChange}
              variant="outlined"
              margin="normal"
              error={!!passwordErrors.currentPassword}
              helperText={passwordErrors.currentPassword}
            />
          </Grid>
          <Grid item xs={12} sm={6}>
            <TextField
              fullWidth
              label="Nueva Contraseña"
              name="newPassword"
              type="password"
              value={passwordData.newPassword}
              onChange={handlePasswordChange}
              variant="outlined"
              margin="normal"
              error={!!passwordErrors.newPassword}
              helperText={passwordErrors.newPassword}
            />
          </Grid>
          <Grid item xs={12} sm={6}>
            <TextField
              fullWidth
              label="Confirmar Nueva Contraseña"
              name="confirmPassword"
              type="password"
              value={passwordData.confirmPassword}
              onChange={handlePasswordChange}
              variant="outlined"
              margin="normal"
              error={!!passwordErrors.confirmPassword}
              helperText={passwordErrors.confirmPassword}
            />
          </Grid>
        </Grid>
        
        <div className={classes.buttonContainer}>
          <Button
            variant="contained"
            color="secondary"
            startIcon={<LockIcon />}
            onClick={handleChangePassword}
            disabled={loading || Object.values(passwordErrors).some(error => error !== '')}
            className={classes.button}
          >
            Cambiar Contraseña
          </Button>
        </div>
      </Paper>

      <Snackbar 
        open={!!success || !!error} 
        autoHideDuration={6000} 
        onClose={handleCloseAlert}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert 
          onClose={handleCloseAlert} 
          severity={success ? "success" : "error"}
          variant="filled"
        >
          {success || error}
        </Alert>
      </Snackbar>
    </div>
  );
} 